import React, { useState } from 'react';
import {
   Box,
   Typography,
   Paper,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   TextField,
   Button,
   Slider,
   Grid,
   Card,
   CardMedia,
   Stack,
   Chip,
   Divider,
   SelectChangeEvent,
   CircularProgress,
} from '@mui/material';
import {
   AutoFixHigh as TransformIcon,
   GridView as GridIcon,
   ViewCarousel as CarouselIcon,
} from '@mui/icons-material';
import { SelectedPhoto } from '../types';
import AIEnhanceImageButton from '../../../../components/AIEnhanceButton';
import { useSubscription } from '../../../../contexts/SubscriptionContext';

// This utilizes the same Stability AI styles already defined in your system
const styleOptions = [
   { value: 'realistic_image', label: 'Realistic Image' },
   { value: 'digital_illustration', label: 'Digital Illustration' },
   { value: 'vector_illustration', label: 'Vector Illustration' },
   // { value: 'icon', label: 'Icon' },
   // { value: 'logo_raster', label: 'Logo Raster' },
];

const realisticSubStylesOptions = [
   { value: 'b_and_w', label: 'B&W' },
   { value: 'enterprise', label: 'Enterprise' },
   { value: 'evening_light', label: 'Evening Light' },
   { value: 'faded_nostalgia', label: 'Faded Nostalgia' },
   { value: 'forest_life', label: 'Forest Life' },
   { value: 'hard_flash', label: 'Hard Flash' },
   { value: 'hdr', label: 'HDR' },
   { value: 'motion_blur', label: 'Motion Blur' },
   { value: 'mystic_naturalism', label: 'Mystic Naturalism' },
   { value: 'natural_light', label: 'Natural Light' },
   { value: 'natural_tones', label: 'Natural Tones' },
   { value: 'organic_calm', label: 'Organic Calm' },
   { value: 'real_life_glow', label: 'Real Life Glow' },
   { value: 'retro_realism', label: 'Retro Realism' },
   { value: 'retro_snapshot', label: 'Retro Snapshot' },
   { value: 'studio_portrait', label: 'Studio Portrait' },
   { value: 'urban_drama', label: 'Urban Drama' },
   { value: 'village_realism', label: 'Village Realism' },
   { value: 'warm_folk', label: 'Warm Folk' },
   { value: null, label: 'None' },
];

const digitalIllustrationSubStylesOptions = [
   { value: '2d_art_poster', label: '2D Art Poster' },
   { value: '2d_art_poster_2', label: '2D Art Poster 2' },
   { value: 'antiquarian', label: 'Antiquarian' },
   { value: 'bold_fantasy', label: 'Bold Fantasy' },
   { value: 'child_book', label: 'Child Book' },
   { value: 'child_books', label: 'Child Books' },
   { value: 'cover', label: 'Cover' },
   { value: 'crosshatch', label: 'Crosshatch' },
   { value: 'digital_engraving', label: 'Digital Engraving' },
   { value: 'engraving_color', label: 'Engraving Color' },
   { value: 'expressionism', label: 'Expressionism' },
   { value: 'freehand_details', label: 'Freehand Details' },
   { value: 'grain', label: 'Grain' },
   { value: 'grain_20', label: 'Grain 20' },
   { value: 'graphic_intensity', label: 'Graphic Intensity' },
   { value: 'hand_drawn', label: 'Hand Drawn' },
   { value: 'hand_drawn_outline', label: 'Hand Drawn Outline' },
   { value: 'handmade_3d', label: 'Handmade 3D' },
   { value: 'hard_comics', label: 'Hard Comics' },
   { value: 'infantile_sketch', label: 'Infantile Sketch' },
   { value: 'long_shadow', label: 'Long Shadow' },
   { value: 'modern_folk', label: 'Modern Folk' },
   { value: 'multicolor', label: 'Multicolor' },
   { value: 'neon_calm', label: 'Neon Calm' },
   { value: 'noir', label: 'Noir' },
   { value: 'nostalgic_pastel', label: 'Nostalgic Pastel' },
   { value: 'outline_details', label: 'Outline Details' },
   { value: 'pastel_gradient', label: 'Pastel Gradient' },
   { value: 'pastel_sketch', label: 'Pastel Sketch' },
   { value: 'pixel_art', label: 'Pixel Art' },
   { value: 'plastic', label: 'Plastic' },
   { value: 'pop_art', label: 'Pop Art' },
   { value: 'pop_renaissance', label: 'Pop Renaissance' },
   { value: 'seamless', label: 'Seamless' },
   { value: 'street_art', label: 'Street Art' },
   { value: 'tablet_sketch', label: 'Tablet Sketch' },
   { value: 'urban_glow', label: 'Urban Glow' },
   { value: 'urban_sketching', label: 'Urban Sketching' },
   { value: 'vanilla_dreams', label: 'Vanilla Dreams' },
   { value: 'young_adult_book', label: 'Young Adult Book' },
   { value: 'young_adult_book_2', label: 'Young Adult Book 2' },
   { value: null, label: 'None' },
];

const vectorIllustrationSubStylesOptions = [
   { value: 'bold_stroke', label: 'Bold Stroke' },
   { value: 'chemistry', label: 'Chemistry' },
   { value: 'colored_stencil', label: 'Colored Stencil' },
   { value: 'contour_pop_art', label: 'Contour Pop Art' },
   { value: 'cosmics', label: 'Cosmics' },
   { value: 'cutout', label: 'Cutout' },
   { value: 'depressive', label: 'Depressive' },
   { value: 'editorial', label: 'Editorial' },
   { value: 'emotional_flat', label: 'Emotional Flat' },
   { value: 'engraving', label: 'Engraving' },
   { value: 'infographical', label: 'Infographical' },
   { value: 'line_art', label: 'Line Art' },
   { value: 'line_circuit', label: 'Line Circuit' },
   { value: 'linocut', label: 'Linocut' },
   { value: 'marker_outline', label: 'Marker Outline' },
   { value: 'mosaic', label: 'Mosaic' },
   { value: 'naivector', label: 'Naivector' },
   { value: 'roundish_flat', label: 'Roundish Flat' },
   { value: 'seamless', label: 'Seamless' },
   { value: 'segmented_colors', label: 'Segmented Colors' },
   { value: 'sharp_contrast', label: 'Sharp Contrast' },
   { value: 'thin', label: 'Thin' },
   { value: 'vector_photo', label: 'Vector Photo' },
   { value: 'vivid_shapes', label: 'Vivid Shapes' },
   { value: null, label: 'None' },
];

const iconSubStylesOptions: any[] = [{ value: null, label: 'None' }];

const logoRasterSubStylesOptions = [
   { value: 'emblem_graffiti', label: 'Emblem Graffiti' },
   { value: 'emblem_pop_art', label: 'Emblem Pop Art' },
   { value: 'emblem_punk', label: 'Emblem Punk' },
   { value: 'emblem_stamp', label: 'Emblem Stamp' },
   { value: 'emblem_vintage', label: 'Emblem Vintage' },
   { value: null, label: 'None' },
];

const transformationTypeOptions = [
   { value: 'replace_background', label: 'Replace Background' },
   { value: 'transform_style', label: 'Transform Style' },
   { value: 'remove_background', label: 'Remove Background' },

   //  { value: 'generate_image', label: 'Generate Image' },
   //  { value: 'inpaint', label: 'Replace specific parts of the image' },
   //  { value: 'crisp', label: 'Enhance Image (sharpness and cleaner)' },
];
interface TransformationPanelProps {
   selectedPhotos: any[];
   onTransform: (options: {
      stylePreset: string;
      subStyle: string;
      prompt: string;
      strength?: number | null;
      transformationType?: string | null;
   }) => void;
   isTransforming: boolean;
   onEnhanceImage: (photo: any, photoId: any) => void;
}

const TransformationPanel: React.FC<TransformationPanelProps> = ({
   selectedPhotos = [],
   onTransform,
   isTransforming,
   onEnhanceImage,
}) => {
   // State management
   const [stylePreset, setStylePreset] = useState<string>('');
   const [prompt, setPrompt] = useState<string>('');
   const [strength, setStrength] = useState<number>(0);
   const [previewMode, setPreviewMode] = useState<'grid' | 'carousel'>('carousel');
   const [subStyle, setSubStyle] = useState<string>('');
   const [transformationType, setTransformationType] = useState<string>('transform_style');
   const { checkSubscriptionStatus } = useSubscription();

   // Handle transformation type change
   const handleTransformationTypeChange = (event: SelectChangeEvent<string>) => {
      setTransformationType(event.target.value);
   };

   // Handle style change
   const handleStyleChange = (event: SelectChangeEvent<string>) => {
      if (event.target.value === 'None') {
         setStylePreset('');
      } else {
         setStylePreset(event.target.value);
      }
   };

   const handleSubStyleChange = (event: SelectChangeEvent<string>) => {
      if (event.target.value === 'None') {
         setSubStyle('');
      } else {
         setSubStyle(event.target.value);
      }
   };

   // Handle strength change
   const handleStrengthChange = (event: Event, newValue: number | number[]) => {
      setStrength(newValue as number);
   };

   // Handle transform button click
   const handleTransformClick = () => {
      if (!checkSubscriptionStatus()) {
         return;
      }

      if (transformationType === 'transform_style') {
         onTransform({
            stylePreset: stylePreset || '',
            subStyle: subStyle || '',
            prompt: prompt || '',
            strength: strength || 0,
            transformationType: transformationType,
         });
      } else if (transformationType === 'replace_background') {
         onTransform({
            stylePreset: stylePreset || '',
            subStyle: subStyle || '',
            prompt: prompt || '',
            strength: null,
            transformationType: transformationType,
         });
      } else if (transformationType === 'remove_background') {
         onTransform({
            stylePreset: '',
            subStyle: '',
            prompt: '',
            strength: null,
            transformationType: transformationType,
         });
      } else if (transformationType === 'generate_image') {
         onTransform({
            stylePreset: stylePreset || '',
            subStyle: subStyle || '',
            prompt: prompt || '',
            strength: null,
            transformationType: transformationType,
         });
      } else if (transformationType === 'inpaint') {
         onTransform({
            stylePreset: '',
            subStyle: '',
            prompt: '',
            strength: null,
            transformationType: transformationType,
         });
      } else if (transformationType === 'crisp') {
         onTransform({
            stylePreset: '',
            subStyle: '',
            prompt: '',
            strength: null,
            transformationType: transformationType,
         });
      }
   };

   return (
      <Paper sx={{ p: 2 }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='h6' gutterBottom>
               Transform Your Photos
            </Typography>

                        {selectedPhotos?.length > 0 && (
               <AIEnhanceImageButton
                  Icon={
                     <Button variant='contained' color='primary'>
                        Enhance Image
                     </Button>
                  }
                  photoId={selectedPhotos[0]?.id}
                  photoUrl={selectedPhotos[0]?.url}
                  onDone={onEnhanceImage}
                  checkSubscription={checkSubscriptionStatus}
               />
            )}
         </Box>

         <>
            {/* <Box sx={{ mb: 2 }}>
               <Typography variant='body2' color='text.secondary'>
                  {selectedPhotos.length} photo{selectedPhotos.length !== 1 ? 's' : ''} selected for
                  transformation
               </Typography>
            </Box> */}

            {selectedPhotos?.length > 0 ? (
               <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {previewMode === 'grid' ? (
                     <Grid container spacing={1}>
                        {selectedPhotos?.map((photo) => (
                           <Grid item xs={4} sm={3} key={photo.id}>
                              <Card sx={{ height: '100%' }}>
                                 <CardMedia
                                    component='img'
                                    height='80'
                                    image={photo.thumbnailUrl || photo.url}
                                    alt={photo.title || 'Selected photo'}
                                    sx={{ objectFit: 'cover' }}
                                 />
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  ) : (
                     <Box sx={{ width: '100%', height: 200, overflow: 'hidden', borderRadius: 1 }}>
                        {selectedPhotos?.length > 0 && (
                           <img
                              src={selectedPhotos[0]?.url}
                              alt={selectedPhotos[0]?.title || 'Selected photo'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                           />
                        )}
                     </Box>
                  )}
               </Box>
            ) : (
               <Typography variant='body2' color='text.secondary' sx={{ my: 2 }}>
                  Select photos from the left panel to begin transformation.
               </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant='subtitle1' gutterBottom>
               Transformation Settings
            </Typography>

            <Grid container spacing={2}>
               <Grid item xs={12}>
                  <FormControl fullWidth size='small'>
                     <InputLabel id='transformation-type-label'>Transformation Type</InputLabel>
                     <Select
                        labelId='transformation-type-label'
                        id='transformation-type'
                        value={transformationType}
                        label='Transformation Type'
                        onChange={handleTransformationTypeChange}
                        disabled={isTransforming}
                     >
                        {transformationTypeOptions.map((type) => (
                           <MenuItem key={type.label} value={type.value}>
                              {type.label}
                           </MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               </Grid>

               {transformationType !== 'replace_background' &&
                  transformationType !== 'remove_background' &&
                  transformationType !== 'crisp' &&
                  transformationType !== 'enhance_image' && (
                     <Grid item xs={12}>
                        <FormControl fullWidth size='small'>
                           <InputLabel id='style-preset-label'>Style Preset (optional) </InputLabel>
                           <Select
                              labelId='style-preset-label'
                              id='style-preset'
                              value={stylePreset}
                              label='Style Preset (optional)'
                              onChange={handleStyleChange}
                              disabled={isTransforming}
                           >
                              {styleOptions.map((style) => (
                                 <MenuItem key={style.label} value={style.value}>
                                    {style.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                  )}
               {transformationType !== 'remove_background' &&
                  transformationType !== 'crisp' &&
                  transformationType !== 'enhance_image' && (
                     <Grid item xs={12}>
                        <FormControl fullWidth size='small'>
                           <InputLabel id='sub-style-label'>Sub-Style (optional)</InputLabel>
                           <Select
                              labelId='sub-style-label'
                              id='sub-style'
                              value={subStyle}
                              label='Sub-Style (optional)'
                              onChange={handleSubStyleChange}
                              disabled={isTransforming}
                           >
                              {(stylePreset === 'realistic_image'
                                 ? realisticSubStylesOptions
                                 : stylePreset === 'digital_illustration'
                                   ? digitalIllustrationSubStylesOptions
                                   : stylePreset === 'vector_illustration'
                                     ? vectorIllustrationSubStylesOptions
                                     : stylePreset === 'icon'
                                       ? iconSubStylesOptions
                                       : stylePreset === 'logo_raster'
                                         ? logoRasterSubStylesOptions
                                         : []
                              ).map((style) => (
                                 <MenuItem key={style.label} value={style.value}>
                                    {style.label}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                  )}

               {transformationType !== 'remove_background' &&
                  transformationType !== 'crisp' &&
                  transformationType !== 'enhance_image' && (
                     <Grid item xs={12}>
                        <TextField
                           fullWidth
                           required
                           label='Custom Prompt'
                           placeholder='Enter additional guidance for the AI transformation...'
                           multiline
                           rows={2}
                           value={prompt}
                           onChange={(e) => setPrompt(e.target.value)}
                           disabled={isTransforming}
                           size='small'
                        />
                     </Grid>
                  )}

               {transformationType !== 'remove_background' &&
                  transformationType !== 'replace_background' &&
                  transformationType !== 'crisp' &&
                  transformationType !== 'generate_image' &&
                  transformationType !== 'enhance_image' && (
                     <Grid item xs={12}>
                        <Typography id='strength-slider-label'>
                           Transformation Strength (optional) : {strength}
                        </Typography>

                        <Typography
                           sx={{ fontSize: '12px' }}
                           variant='caption'
                           color='text.secondary'
                           gutterBottom
                        >
                           (Closer to 0 or 0 is more similar to the original image)
                        </Typography>

                        <Slider
                           aria-labelledby='strength-slider-label'
                           // value={strength}
                           onChange={handleStrengthChange}
                           value={strength}
                           defaultValue={0}
                           step={0.1}
                           min={0}
                           max={1}
                           disabled={isTransforming}
                        />
                     </Grid>
                  )}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={
                     isTransforming ? (
                        <CircularProgress size={20} color='inherit' />
                     ) : (
                        <TransformIcon />
                     )
                  }
                  onClick={handleTransformClick}
                  disabled={isTransforming || selectedPhotos?.length === 0}
                  size='large'
               >
                  {isTransforming ? 'Transforming...' : 'Transform Photos'}
               </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
               <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', textAlign: 'center' }}
               >
                  enhance_image' ? ` Your photos will be transformed using preset style $
                  {prompt && ' with your custom prompt'}
               </Typography>
            </Box>
         </>
         {/* )} */}
      </Paper>
   );
};

export default TransformationPanel;
